import { connect } from 'react-redux';
import React, { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button,
  Container,
  InnerContain,
  ActionsContain,
  ButtonsContain
} from './CreateStyled';

import Form1Banner from './Form1Banner';
import Stepper, { Step } from './Stepper';
import CriticalError from './CriticalError';
import CreateFrom from './CreateFrom';
import { withErrorBoundary } from './ErrorBoundary';

import * as Steps from './steps';
import ErrorDisplay from 'components/ErrorDisplay';

import { errorSet, errorClear } from 'actions/error';
import { getItems, submitOrder, deleteOrder } from 'actions/orders';

import { FORM1, STRATA, COUNCIL_SEARCH } from 'constants/orders';

import config from 'config';

function getSupportedStates (orderType) {
  return config.features[orderType].states
}

export const CreateContainer = (props) => {
  const createFromRef = useRef(null);
  const history = useHistory();

  useEffect(
    function clearErrors () {
      props.dispatchErrorClear();
      return () => props.dispatchErrorClear();
    }, 
    [ props.dispatchErrorClear ]
  );

  useEffect(
    function clearOrderUnmount () {
      return () => props.dispatchOrderDelete();
    }, 
    [ props.dispatchOrderDelete ]
  );

  useEffect(
    function getForm1Items () {
      if (props.orderType === FORM1) {
        props.dispatchGetItems();
      }
    }, 
    [ props.orderType, props.dispatchGetItems ]
  );

  useEffect(
    function checkCreateFrom () {
      if (!!props.order.createFrom && !createFromRef.current) {
        createFromRef.current = true;
        setCreateFrom(true)
      }
    },
    [ props.order]
  )

  const [step, setStep] = useState(0);
  const [createFrom, setCreateFrom ] = useState(false);

  const nextStep = () => setStep((step) => step + 1);
  const prevStep = () => setStep((step) => (step - 1 > 0 ? step - 1 : 0));

  const handleReset = () => {
    props.dispatchErrorClear();
    setStep(0);
  };

  const handleBack = (onBack) => {
    if (props.order.createFrom) {
      // for now prevent back and just navigate to orders when backing out of prepopulated order
      return history.replace('/orders');
    }

    if (onBack) {
      onBack();
    }

    props.dispatchErrorClear();
    prevStep();
  };

  const handleSubmit = (onSubmit) => {
    if (onSubmit) {
      var result = onSubmit();
      if (result === false) return; //prevent submit
    }

    props.dispatchSubmitOrder(props.orderType);
  }

  const handleValidateAll = (validation = []) => {
    props.dispatchErrorClear();

    const validateAll = () => validation.reduce((m, validate) => {
      const [isValid, errors, error] = validate();

      return [ m[0] && isValid, { ...m[1], ...errors }, error ];
    }, [ true, {}, '']);


    const [ isValid, errors, error] = validateAll();

    if (!isValid) {
      props.dispatchError({ inputErrors: errors, message: error || '' });
    }

    return isValid;
  };

  const handleNext = (onNext) => {
    props.dispatchErrorClear();

    if (!onNext) {
      return nextStep();
    }

    const [isValid, errors, error] = onNext();

    isValid
      ? nextStep()
      : props.dispatchError({ inputErrors: errors, message: error || '' });
  };

  const renderActions = ({
    onNext,
    onBack,
    onSubmit,
    submit,
    hideBack,
    hideNext,
    backLabel,
    reset,
    nextLabel,
    disableNext,
    cardRequired,
    handleCard
  }) => {
    var clickHandler;


    if (!disableNext) {
      clickHandler = submit
        ? handleSubmit.bind(null, onSubmit)
        : handleNext.bind(null, onNext) 
    }

    if (cardRequired) clickHandler = handleCard;

    return (
      <ActionsContain>
        <ErrorDisplay message={props.errorMessage} />
        <ButtonsContain justifyEnd={hideBack}>
          {!hideBack && (
            <Button
              secondary
              onClick={reset ? handleReset : handleBack.bind(null, onBack)}
            >
              {backLabel || 'Back'}
            </Button>
          )}
          {!hideNext && (
            <Button
              disabled={disableNext}
              onClick={clickHandler}
            >
              {nextLabel || 'Next'}
            </Button>
          )}
        </ButtonsContain>
      </ActionsContain>
    );
  };

  // TODO fix up step handling to be array of applicable steps based on state / order type
  // stepper / render step can just use that and no need for all this duplication of logic / handling between stepper / render step
  const renderStep = () => {
    const getStrataStep = (stepProps = {}) => {
      switch (step) {
        case 0: return Steps.StepAddress;
        case 1: return Steps.StepAddressConfirm;
        case 2: return Steps.StepStrata;
        case 3: return Steps.StepTitleSearch;
        case 4: return Steps.StepCheckout;
        case 5: return Steps.StepComplete;
        default: return null;
      }
    };

    const getForm1Step = (stepProps = {}) => {
      switch (step) {
        case 0: return Steps.StepAddress;
        case 1: return Steps.StepAddressConfirm;
        case 2: return Steps.StepStrata;
        case 3: return Steps.StepCheckout;
        case 4: return Steps.StepComplete;
        default: return null;
      }
    };

    const getCouncilSearchStep = (stepProps = {}) => {
       switch (step) {
        case 0: return Steps.StepAddress;
        case 1: return Steps.StepAddressConfirm;
        case 2: return Steps.StepCouncil;
        case 3: return Steps.StepCertificates;
        case 4: return Steps.StepTitleSearch;
        case 5: return Steps.StepStrata;
        case 6: return Steps.StepCheckout;
        case 7: return Steps.StepComplete;
        default: return null;
      }
    };

    const getStepComponent = () => {
      switch (props.orderType) {
        case FORM1: return getForm1Step();
        case STRATA: return getStrataStep();
        default: return getCouncilSearchStep();
      }
    }

    if (props.criticalError) {
      return <CriticalError renderActions={renderActions} />;
    }

    const Component = getStepComponent();
    
    if (!Component) {
      return null;
    }

    return (
      <Component
        nextStep={nextStep}
        orderType={props.orderType}
        renderActions={renderActions}
        onValidateAll={handleValidateAll}
        supportedStates={getSupportedStates(props.orderType)}
      />
    )
  };

  const renderStepper = () => {
    const renderStrataSteps = () => {
      return (
        <Stepper step={step}>
          <Step label="Address"></Step>
          <Step label="Address Confirm"></Step>
          <Step label="Strata/CT"></Step>
          <Step label="Title Search"></Step>     
          <Step label="Checkout"></Step>
        </Stepper>
      );
    };

    const renderCouncilSearchSteps = () => {
      return (
        <Stepper step={step}>
          <Step completeStep={1} label="Address Details"></Step>
          <Step hidden label="Address Confirm"></Step>
          <Step label="Council Select"></Step>
          <Step label="Order Details"></Step>
          <Step label="Title Search"></Step>        
          <Step label="Strata/CT"></Step>
          <Step label="Checkout"></Step>
        </Stepper>
      );
    };

    const renderForm1Steps = () => {
      return (
        <Stepper step={step}>
          <Step label="Address"></Step>
          <Step label="Address Confirm"></Step>
          <Step label="Strata/CT"></Step>
          <Step label="Checkout"></Step>
        </Stepper>
      );
    };

    if (props.orderType === FORM1) {
      return renderForm1Steps();
    }

    if (props.orderType === STRATA) {
      return renderStrataSteps();
    }

    return renderCouncilSearchSteps();
  };

  if (createFrom) {
    return (
      <CreateFrom {...props} onClose={() => setCreateFrom(false) } setStep={setStep} />
    )
  }

  return (
    <Container>
      <InnerContain>
        {renderStepper()}
        {props.orderType === FORM1 && <Form1Banner session={props.session} />}
        {renderStep()}
      </InnerContain>
    </Container>
  );
};

const mapDispatchToProps = {
  dispatchError: errorSet,
  dispatchGetItems: getItems,
  dispatchErrorClear: errorClear,
  dispatchOrderDelete: deleteOrder,
  dispatchSubmitOrder: submitOrder
};

const mapStateToProps = (state) => ({
  session: state.session,
  order: state.pendingOrder.pending,
  errorMessage: state.error.message,
  criticalError: state.error.critical
});

CreateContainer.defaultProps = {
  orderType: COUNCIL_SEARCH
};

export default withErrorBoundary(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CreateContainer)
);